import type { ReactNode } from 'react';
import type { InterviewInviteListItemDto } from '@sara/api-client';
import { Link as LinkIcon, Check } from 'lucide-react';
import { Button } from '@sara/ui/components/button';
import { DataPanel } from '@sara/ui/components/data-panel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sara/ui/components/table';
import { useCopyToClipboard } from '../../../../hooks/useCopyToClipboard';
import { InterviewStatusBadge } from '../../../interviews/components/InterviewStatusBadge';
import { formatDate } from '../../../../utils/formatDate';

function CopyLinkCell({ url }: { url: string }) {
  const { copied, copy } = useCopyToClipboard();
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-muted-foreground hover:text-foreground"
      aria-label="Copy interview link"
      onClick={(e) => {
        e.stopPropagation();
        void copy(url);
      }}
    >
      {copied ? (
        <Check className="h-4 w-4 text-success" strokeWidth={1.6} />
      ) : (
        <LinkIcon className="h-4 w-4" strokeWidth={1.6} />
      )}
    </Button>
  );
}

interface CandidateInterviewListTableProps {
  items: InterviewInviteListItemDto[];
  onRowClick: (invite: InterviewInviteListItemDto) => void;
  isFetching?: boolean;
  footer?: ReactNode;
}

export function CandidateInterviewListTable({
  items,
  onRowClick,
  isFetching,
  footer,
}: CandidateInterviewListTableProps) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-20 text-center">
        <p className="font-display text-xl font-medium text-foreground">No interviews yet.</p>
        <p className="mt-2 max-w-xs text-sm text-muted-foreground">
          Invite this candidate to an interview to get started.
        </p>
      </div>
    );
  }

  return (
    <DataPanel className={isFetching ? 'opacity-60 transition-opacity' : undefined}>
      <DataPanel.Body>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Template</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((invite) => (
              <TableRow
                key={invite.id}
                onClick={() => onRowClick(invite)}
                className="cursor-pointer"
              >
                <TableCell className="text-[13.5px] text-foreground">
                  {invite.interviewType.name}
                </TableCell>
                <TableCell>
                  <InterviewStatusBadge status={invite.status} />
                </TableCell>
                <TableCell className="text-[13px] tabular-nums text-muted-foreground">
                  {formatDate(invite.createdAt)}
                </TableCell>
                <TableCell className="text-[13px] tabular-nums text-muted-foreground">
                  {formatDate(invite.expiresAt)}
                </TableCell>
                <TableCell className="text-right">
                  <CopyLinkCell url={invite.url} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataPanel.Body>
      {footer && <DataPanel.Footer>{footer}</DataPanel.Footer>}
    </DataPanel>
  );
}
